import { useCallback } from 'react';

import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useEditCanvasStore } from '../../store';
import { type AppNode } from '../../types';
import { compositeGroup } from '../../utils/group/compositeGroup';
import { computeGroupBounds, getGroupMembers } from '../../utils/group/nodeGrouping';
import { computeRowAwareAppendPosition } from '../../utils/layout/nodeLayout';
import { commands } from '../commands/commands';
import { useCommandHistory } from '../commands/hooks/useCommandHistory';
import { useNodeLayout } from '../useNodeLayout';
import { useSelectedCanvasNodes } from '../useSelectedCanvasNodes';
import { useUploadAndPreload } from '../useUploadAndPreload';

const FLATTENED_FILE_NAME = 'flattened.png';
const FLATTENED_CONTENT_TYPE = 'image/png';

export type FlattenGroupOptions = {
  /** Flatten this group's members instead of the current canvas selection. */
  groupId?: string;
};

const resolveFlattenMembers = (groupId: string | undefined, selectedNodes: AppNode[]): AppNode[] => {
  if (!groupId) {
    return selectedNodes;
  }

  return getGroupMembers(useEditCanvasStore.getState().nodes, groupId);
};

export const useFlattenAction = () => {
  const selectedNodes = useSelectedCanvasNodes();
  const { uploadAndPreload } = useUploadAndPreload();
  const { execute } = useCommandHistory();
  const { showToast } = useToasts();
  const { getContentNodeRects, getGap, getMaxRowWidth } = useNodeLayout();
  const isFlattening = useEditCanvasStore((s) => s.isFlattening);

  const handleFlatten = useCallback(
    async (options?: FlattenGroupOptions): Promise<AppNode | undefined> => {
      if (useEditCanvasStore.getState().isFlattening) {
        return undefined;
      }

      const { canvasId: startCanvasId, setIsFlattening } = useEditCanvasStore.getState();
      setIsFlattening(true);

      try {
        const members = resolveFlattenMembers(options?.groupId, selectedNodes);
        const bounds = computeGroupBounds(members);

        if (!bounds) {
          throw new Error('Cannot flatten an empty group');
        }

        const blob = await compositeGroup(members);
        const file = new File([blob], FLATTENED_FILE_NAME, { type: FLATTENED_CONTENT_TYPE });
        const results = await uploadAndPreload([{ contentType: FLATTENED_CONTENT_TYPE, file }]);
        const result = results[0];

        if (result?.status !== 'fulfilled') {
          throw new Error('Upload failed');
        }

        // Bail if the user switched canvases mid-flatten — adding to the new
        // canvas's nodes array would silently land the result on the wrong canvas.
        if (useEditCanvasStore.getState().canvasId !== startCanvasId) {
          return undefined;
        }

        const newNodeSize = { height: bounds.height, width: bounds.width };
        const rects = getContentNodeRects();
        const groupRects = rects.filter((r) => members.some((n) => n.id === r.id));
        // Anchor the append-row to the group's rightmost member so layered flattens
        // step rightward (or wrap) instead of stacking on top of the previous result.
        const rightEdge = (rect: (typeof groupRects)[number]) => rect.x + rect.width;
        let rightmostGroupMember: (typeof groupRects)[number] | undefined;

        for (const rect of groupRects) {
          if (!rightmostGroupMember || rightEdge(rect) > rightEdge(rightmostGroupMember)) {
            rightmostGroupMember = rect;
          }
        }

        const { position } = computeRowAwareAppendPosition({
          allNodes: rects,
          gap: getGap(),
          maxRowWidth: getMaxRowWidth(),
          newNodeSize,
          targetRowNodeId: rightmostGroupMember?.id,
        });

        const imageNode: AppNode = {
          data: {
            fileName: FLATTENED_FILE_NAME,
            media: { image: result.cdnUrl, original: result.cdnUrl },
            source: 'manual-edit',
            type: 'image',
          },
          id: crypto.randomUUID(),
          position: { x: position.x, y: bounds.minY },
          style: newNodeSize,
          type: 'image',
        };

        execute(commands.addNodes([imageNode], { zoomToFit: false }));

        return imageNode;
      } catch (error) {
        reportErrorToBugsnag({
          context: 'useFlattenAction: Flatten failed',
          error: error instanceof Error ? error : new Error(String(error)),
        });
        showToast('Failed to flatten group', { color: 'red' });

        return undefined;
      } finally {
        // isFlattening is per-canvas (reset by setCanvasId), so only clear it if
        // we're still on the original canvas — otherwise we'd wipe the flag for
        // an unrelated flatten that started on the new canvas.
        if (useEditCanvasStore.getState().canvasId === startCanvasId) {
          setIsFlattening(false);
        }
      }
    },
    [execute, getContentNodeRects, getGap, getMaxRowWidth, selectedNodes, showToast, uploadAndPreload],
  );

  return { handleFlatten, isFlattening };
};
